import { useState, useCallback, useEffect, useRef } from 'react';
import { PageNode, PageMeta, Connection, ConnectionSide, PageType, DEFAULT_PAGE_COLORS } from '@/types/sitemap';
import { sitemapService } from '@/lib/sitemapService';

const generateId = () => Math.random().toString(36).substring(2, 10);

const SNAP_SIZE = 20;
const NODE_WIDTH = 280;
const NODE_DEFAULT_HEIGHT = 300;
const H_GAP = 60;
const V_GAP_HOME = 100;
const V_GAP_DEFAULT = 40;

const snap = (v: number) => Math.round(v / SNAP_SIZE) * SNAP_SIZE;

const defaultSections: Record<PageType, string[]> = {
  home: ['Header', 'Hero', 'Features Grid', 'Testimonials', 'CTA Section', 'Footer'],
  blog: ['Header', 'Blog Grid', 'Newsletter Signup', 'Footer'],
  'blog-archive': ['Header', 'Blog Grid', 'Newsletter Signup', 'Footer'],
  'blog-single': ['Header', 'Content Block', 'Newsletter Signup', 'Footer'],
  about: ['Header', 'Hero with Image', 'Team Section', 'Timeline', 'Footer'],
  contact: ['Header', 'Contact Form', 'Map', 'FAQ', 'Footer'],
  landing: ['Header', 'Hero', 'Features List', 'Testimonials', 'Pricing Table', 'CTA Section', 'Footer'],
  pricing: ['Header', 'Pricing Table', 'Comparison Table', 'FAQ', 'CTA Section', 'Footer'],
  product: ['Header', 'Hero with Image', 'Features Grid', 'Testimonials', 'Pricing Table', 'CTA Section', 'Footer'],
  login: ['Header', 'Content Block', 'Footer Minimal'],
  signup: ['Header', 'Content Block', 'Footer Minimal'],
  dashboard: ['Sidebar Nav', 'Stats', 'Content Block'],
  profile: ['Header', 'Content Block', 'Footer'],
  settings: ['Header', 'Tab Navigation', 'Content Block', 'Footer'],
  portfolio: ['Header', 'Hero', 'Gallery', 'Testimonials', 'CTA Section', 'Footer'],
  services: ['Header', 'Hero', 'Features List', 'Testimonials', 'CTA Section', 'Footer'],
  careers: ['Header', 'Hero with Image', 'Content Block', 'Team Section', 'CTA Section', 'Footer'],
  faq: ['Header', 'FAQ', 'CTA Section', 'Footer'],
  terms: ['Header', 'Content Block', 'Footer'],
  privacy: ['Header', 'Content Block', 'Footer'],
  '404': ['Header', 'Content Block', 'Footer Minimal'],
  default: ['Header', 'Content Block', 'Footer'],
};

const sectionDescriptions: Record<string, string> = {
  Header: 'Your brand navigation and logo area.',
  Hero: 'A bold intro section to capture attention.',
  'Hero with Video': 'Hero section featuring a video walkthrough.',
  'Hero with Image': 'Hero section with a prominent visual.',
  'Split Hero': 'Half content, half visual layout.',
  'Features Grid': 'Grid layout showcasing key features.',
  'Features List': 'List-style feature highlights.',
  'Content Block': 'General-purpose content section.',
  'Blog Grid': 'Grid layout for blog posts.',
  'Blog List': 'List layout for blog articles.',
  'Team Section': 'Showcase your team members.',
  Gallery: 'Visual showcase of work or images.',
  Timeline: 'Chronological milestones or events.',
  Stats: 'Key metrics and statistics display.',
  'Comparison Table': 'Side-by-side feature comparison.',
  Testimonials: 'Customer quotes and social proof.',
  'Logo Cloud': 'Partner and client logo display.',
  Reviews: 'User ratings and reviews section.',
  'Case Studies': 'In-depth customer success stories.',
  'CTA Section': 'Call-to-action to drive conversions.',
  'Pricing Table': 'Pricing plans and tiers display.',
  'Newsletter Signup': 'Email subscription form.',
  'Contact Form': 'User inquiry and contact form.',
  'Lead Capture': 'Lead generation form section.',
  Footer: 'Site-wide footer with links and info.',
  'Footer Minimal': 'Simplified footer section.',
  'Footer with CTA': 'Footer with a call-to-action.',
  FAQ: 'Frequently asked questions section.',
  Map: 'Location map display.',
  Divider: 'Visual separator between sections.',
  Banner: 'Promotional banner section.',
  Navbar: 'Main navigation bar.',
  Breadcrumbs: 'Breadcrumb navigation trail.',
  'Sidebar Nav': 'Sidebar navigation for inner pages.',
  'Tab Navigation': 'Tabbed navigation interface.',
};

const sampleCopy: Record<string, string> = {
  Header: 'Your brand navigation and logo area.',
  Hero: 'Welcome to our platform — the easiest way to build and ship your next big idea.',
  'Hero with Video': 'See our product in action with a compelling video walkthrough.',
  'Hero with Image': 'A stunning visual introduction to your brand story.',
  'Split Hero': 'Half content, half visual — perfectly balanced storytelling.',
  'Features Grid': 'Powerful tools designed to help you move faster and build smarter.',
  'Features List': 'Everything you need, nothing you don\'t. Here\'s what we offer.',
  'Content Block': 'Main content section with your key message.',
  'Blog Grid': 'Browse our collection of articles, guides, and insights.',
  'Blog List': 'Latest articles and stories from our team.',
  'Team Section': 'Meet the passionate people behind the product.',
  Gallery: 'A visual showcase of our work and achievements.',
  Timeline: '2020: Founded → 2022: 10k users → 2024: Series A',
  Stats: '10K+ users, 99.9% uptime, 50+ countries served.',
  'Comparison Table': 'See how we stack up against the competition.',
  Testimonials: '"This product changed how we work. Highly recommended!" — Jane D.',
  'Logo Cloud': 'Trusted by leading companies worldwide.',
  Reviews: '⭐⭐⭐⭐⭐ Rated 4.9 out of 5 by thousands of users.',
  'Case Studies': 'See how Company X increased revenue by 300% using our platform.',
  'CTA Section': 'Ready to get started? Sign up today and launch in minutes.',
  'Pricing Table': 'Simple, transparent pricing. No hidden fees.',
  'Newsletter Signup': 'Stay in the loop — get weekly updates delivered to your inbox.',
  'Contact Form': 'Have questions? Reach out and we\'ll get back within 24 hours.',
  'Lead Capture': 'Get a free demo — enter your email to get started.',
  Footer: '© 2026 Your Company. All rights reserved.',
  'Footer Minimal': '© 2026 Company',
  'Footer with CTA': 'Start your free trial today. © 2026 Company.',
  FAQ: 'Find answers to common questions about our product and services.',
  Map: 'Visit us at 123 Innovation Drive, San Francisco, CA 94107.',
  Divider: '—',
  Banner: '🎉 Special offer: 50% off for early adopters!',
  Navbar: 'Home | Features | Pricing | Blog | Contact',
  Breadcrumbs: 'Home > Section > Current Page',
  'Sidebar Nav': 'Contextual navigation for inner pages.',
  'Tab Navigation': 'Tab 1 | Tab 2 | Tab 3',
};

function getPageHeight(pageId: string): number {
  const el = document.querySelector(`[data-page-id="${pageId}"]`) as HTMLElement | null;
  return el ? el.offsetHeight : NODE_DEFAULT_HEIGHT;
}

export function useSitemap(sitemapId?: string, workspaceAccentColor?: string) {
  const [isLoading, setIsLoading] = useState(!!sitemapId);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstLoad = useRef(true);

  const [pages, setPages] = useState<PageNode[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [connectingFrom, setConnectingFrom] = useState<{ pageId: string; side: ConnectionSide } | null>(null);
  const [sitemapName, setSitemapName] = useState<string>('Sitemap Builder');

  // Update homepage color when workspaceAccentColor becomes available (for new unsaved sitemaps)
  useEffect(() => {
    if (!workspaceAccentColor || !isFirstLoad.current || sitemapId) return;
    
    setPages(prev => prev.map(p => {
      if (p.type === 'home' && (p.color === DEFAULT_PAGE_COLORS.home)) {
        return { ...p, color: workspaceAccentColor };
      }
      return p;
    }));
  }, [workspaceAccentColor, sitemapId]);

  // Load sitemap from Supabase when sitemapId is provided
  useEffect(() => {
    if (!sitemapId) return;
    setIsLoading(true);
    sitemapService.getSitemapById(sitemapId).then(data => {
      if (data) {
        if (data.name) setSitemapName(data.name);
        if (data.pages && data.pages.length > 0) setPages(data.pages);
        if (data.connections) setConnections(data.connections);
      }
    }).catch(console.error).finally(() => {
      setIsLoading(false);
      // Mark first load as done so auto-save doesn't trigger immediately
      setTimeout(() => { isFirstLoad.current = false; }, 500);
    });
  }, [sitemapId]);

  const save = useCallback(async () => {
    if (!sitemapId) return;
    setIsSaving(true);
    try {
      // Extract homepage featured image as thumbnail
      const homepage = pages.find(p => p.type === 'home');
      const thumbnailUrl = homepage?.meta?.featuredImage || null;
      await sitemapService.saveSitemap(sitemapId, pages, connections, thumbnailUrl ?? undefined);
      setLastSaved(new Date());
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  }, [sitemapId, pages, connections]);


  const addPage = useCallback((type: PageType, name: string, x?: number, y?: number) => {
    const offset = pages.length * 50;
    const newPage: PageNode = {
      id: generateId(),
      name,
      type,
      color: type === 'home' ? (workspaceAccentColor || DEFAULT_PAGE_COLORS[type]) : DEFAULT_PAGE_COLORS[type],
      x: snap(x ?? (200 + offset)),
      y: snap(y ?? (200 + offset)),
      sections: defaultSections[type].map(sName => ({
        id: generateId(),
        name: sName,
        description: sectionDescriptions[sName] || '',
        copy: '',
      })),
    };
    setPages(prev => [...prev, newPage]);
  }, [pages.length, workspaceAccentColor]);

  const addPageRelative = useCallback((parentId: string, direction: ConnectionSide, type: PageType, name: string) => {
    setPages(prev => {
      const parent = prev.find(p => p.id === parentId);
      if (!parent) return prev;

      const parentH = getPageHeight(parentId);
      const isHomeParent = parent.type === 'home';
      const vGap = isHomeParent ? V_GAP_HOME : V_GAP_DEFAULT;
      let newX = parent.x;
      let newY = parent.y;

      // Calculate position based on direction
      if (direction === 'right') {
        newX = parent.x + NODE_WIDTH + H_GAP;
        newY = parent.y;
      } else if (direction === 'left') {
        newX = parent.x - NODE_WIDTH - H_GAP;
        newY = parent.y;
      } else if (direction === 'bottom') {
        newX = parent.x;
        newY = parent.y + parentH + vGap;
      } else if (direction === 'top') {
        newX = parent.x;
        newY = parent.y - NODE_DEFAULT_HEIGHT - vGap;
      }

      // Check for collision with existing pages (exclude parent) using proper AABB
      const isOccupied = (x: number, y: number) =>
        prev.some(p => {
          if (p.id === parentId) return false;
          const pH = getPageHeight(p.id);
          const newRight = x + NODE_WIDTH;
          const newBottom = y + NODE_DEFAULT_HEIGHT;
          const pRight = p.x + NODE_WIDTH;
          const pBottom = p.y + pH;
          // AABB overlap with 20px margin
          return x < pRight + 20 && newRight > p.x - 20 && y < pBottom + 20 && newBottom > p.y - 20;
        });

      // If occupied, shift further in the same direction
      let attempts = 0;
      while (isOccupied(newX, newY) && attempts < 10) {
        if (direction === 'bottom') {
          newY += NODE_DEFAULT_HEIGHT + vGap;
        } else if (direction === 'top') {
          newY -= NODE_DEFAULT_HEIGHT + vGap;
        } else if (direction === 'right') {
          newX += NODE_WIDTH + H_GAP;
        } else {
          newX -= NODE_WIDTH + H_GAP;
        }
        attempts++;
      }

      newX = snap(newX);
      newY = snap(newY);

      const newId = generateId();
      const newPage: PageNode = {
        id: newId,
        name,
        type,
        color: type === 'home' ? (workspaceAccentColor || DEFAULT_PAGE_COLORS[type]) : DEFAULT_PAGE_COLORS[type],
        x: newX,
        y: newY,
        parentId,
        sections: defaultSections[type].map(sName => ({
          id: generateId(),
          name: sName,
          description: sectionDescriptions[sName] || '',
          copy: '',
        })),
      };

      // Auto-create connection
      const fromSide = direction;
      const toSide: ConnectionSide = direction === 'right' ? 'left' : direction === 'left' ? 'right' : direction === 'bottom' ? 'top' : 'bottom';

      setTimeout(() => {
        setConnections(prevC => [...prevC, {
          id: generateId(),
          fromId: parentId,
          toId: newId,
          fromSide: fromSide,
          toSide: toSide,
          style: 'curved',
        }]);
      }, 0);

      return [...prev, newPage];
    });
  }, [workspaceAccentColor]);

  const updatePagePosition = useCallback((id: string, x: number, y: number) => {
    setPages(prev => prev.map(p => p.id === id ? { ...p, x: snap(x), y: snap(y) } : p));
  }, []);

  const batchUpdatePositions = useCallback((updates: { id: string; x: number; y: number }[]) => {
    setPages(prev => prev.map(p => {
      const update = updates.find(u => u.id === p.id);
      return update ? { ...p, x: snap(update.x), y: snap(update.y) } : p;
    }));
  }, []);

  const deletePage = useCallback((id: string) => {
    setPages(prev => prev.filter(p => p.id !== id));
    setConnections(prev => prev.filter(c => c.fromId !== id && c.toId !== id));
  }, []);

  const updatePageColor = useCallback((id: string, color: string) => {
    setPages(prev => prev.map(p => p.id === id ? { ...p, color } : p));
  }, []);

  const addSection = useCallback((pageId: string, name: string) => {
    setPages(prev => prev.map(p => p.id === pageId ? {
      ...p,
      sections: [...p.sections, { id: generateId(), name, description: sectionDescriptions[name] || '', copy: '' }],
    } : p));
  }, []);

  const removeSection = useCallback((pageId: string, sectionId: string) => {
    setPages(prev => prev.map(p => p.id === pageId ? {
      ...p,
      sections: p.sections.filter(s => s.id !== sectionId),
    } : p));
  }, []);

  const duplicateSection = useCallback((pageId: string, sectionId: string) => {
    setPages(prev => prev.map(p => {
      if (p.id !== pageId) return p;
      const idx = p.sections.findIndex(s => s.id === sectionId);
      if (idx === -1) return p;
      const source = p.sections[idx];
      const copy = { ...source, id: generateId(), name: `${source.name}` };
      const sections = [...p.sections];
      sections.splice(idx + 1, 0, copy);
      return { ...p, sections };
    }));
  }, []);

  const moveSectionToPage = useCallback((fromPageId: string, sectionId: string, toPageId: string, toIndex?: number) => {
    setPages(prev => {
      const fromPage = prev.find(p => p.id === fromPageId);
      if (!fromPage) return prev;
      const section = fromPage.sections.find(s => s.id === sectionId);
      if (!section) return prev;
      return prev.map(p => {
        if (p.id === fromPageId) return { ...p, sections: p.sections.filter(s => s.id !== sectionId) };
        if (p.id === toPageId) {
          const sections = [...p.sections];
          if (toIndex !== undefined && toIndex >= 0 && toIndex <= sections.length) {
            sections.splice(toIndex, 0, { ...section });
          } else {
            sections.push({ ...section });
          }
          return { ...p, sections };
        }
        return p;
      });
    });
  }, []);

  const reorderSections = useCallback((pageId: string, fromIndex: number, toIndex: number) => {
    setPages(prev => prev.map(p => {
      if (p.id !== pageId) return p;
      const sections = [...p.sections];
      const [moved] = sections.splice(fromIndex, 1);
      sections.splice(toIndex, 0, moved);
      return { ...p, sections };
    }));
  }, []);

  const generateCopy = useCallback((pageId: string, sectionId: string) => {
    setPages(prev => prev.map(p => p.id === pageId ? {
      ...p,
      sections: p.sections.map(s => s.id === sectionId ? {
        ...s,
        copy: sampleCopy[s.name] || `Compelling copy for the "${s.name}" section that engages visitors and drives action.`,
      } : s),
    } : p));
  }, []);

  const generateAllCopy = useCallback((pageId: string) => {
    setPages(prev => prev.map(p => p.id === pageId ? {
      ...p,
      sections: p.sections.map(s => ({
        ...s,
        copy: sampleCopy[s.name] || `Compelling copy for the "${s.name}" section that engages visitors and drives action.`,
      })),
    } : p));
  }, []);

  const updateSectionCopy = useCallback((pageId: string, sectionId: string, copy: string) => {
    setPages(prev => prev.map(p => p.id === pageId ? {
      ...p,
      sections: p.sections.map(s => s.id === sectionId ? { ...s, copy } : s),
    } : p));
  }, []);

  const updatePageMeta = useCallback((pageId: string, meta: Partial<PageMeta>) => {
    setPages(prev => prev.map(p => p.id === pageId ? {
      ...p,
      meta: { ...(p.meta || { metaTitle: '', metaDescription: '', jsonLd: '', featuredImage: '', featuredImageAlt: '' }), ...meta },
    } : p));
  }, []);

  const updateSectionColor = useCallback((pageId: string, sectionId: string, color: string) => {
    setPages(prev => prev.map(p => p.id === pageId ? {
      ...p,
      sections: p.sections.map(s => s.id === sectionId ? { ...s, color } : s),
    } : p));
  }, []);

  const startConnection = useCallback((pageId: string, side: ConnectionSide) => {
    setConnectingFrom({ pageId, side });
  }, []);

  const completeConnection = useCallback((toId: string, toSide: ConnectionSide) => {
    if (connectingFrom && connectingFrom.pageId !== toId) {
      const exists = connections.some(
        c => (c.fromId === connectingFrom.pageId && c.toId === toId) ||
             (c.fromId === toId && c.toId === connectingFrom.pageId)
      );
      if (!exists) {
        setConnections(prev => [...prev, {
          id: generateId(),
          fromId: connectingFrom.pageId,
          toId,
          fromSide: connectingFrom.side,
          toSide,
          style: 'curved',
        }]);
      }
    }
    setConnectingFrom(null);
  }, [connectingFrom, connections]);

  const removeConnection = useCallback((connectionId: string) => {
    setConnections(prev => prev.filter(c => c.id !== connectionId));
  }, []);

  const updateConnectionStyle = useCallback((connectionId: string, style: Connection['style']) => {
    setConnections(prev => prev.map(c => c.id === connectionId ? { ...c, style } : c));
  }, []);

  const cancelConnection = useCallback(() => {
    setConnectingFrom(null);
  }, []);

  const duplicatePage = useCallback((id: string) => {
    setPages(prev => {
      const source = prev.find(p => p.id === id);
      if (!source) return prev;
      const newPage: PageNode = {
        ...source,
        id: generateId(),
        name: `${source.name} (copy)`,
        x: snap(source.x + 40),
        y: snap(source.y + 40),
        sections: source.sections.map(s => ({ ...s, id: generateId() })),
      };
      return [...prev, newPage];
    });
  }, []);

  // Swap positions AND connections of two sibling pages
  const swapPagePositions = useCallback((pageId1: string, pageId2: string) => {
    setPages(prev => {
      const p1 = prev.find(p => p.id === pageId1);
      const p2 = prev.find(p => p.id === pageId2);
      if (!p1 || !p2) return prev;
      return prev.map(p => {
        if (p.id === pageId1) return { ...p, x: p2.x, y: p2.y };
        if (p.id === pageId2) return { ...p, x: p1.x, y: p1.y };
        return p;
      });
    });
    // Swap the two page IDs throughout all connections so connections follow positions
    setConnections(prev => prev.map(c => {
      let { fromId, toId } = c;
      if (fromId === pageId1) fromId = pageId2;
      else if (fromId === pageId2) fromId = pageId1;
      if (toId === pageId1) toId = pageId2;
      else if (toId === pageId2) toId = pageId1;
      return { ...c, fromId, toId };
    }));
  }, []);

  return {
    sitemapName,
    pages,
    connections,
    connectingFrom,
    addPage,
    addPageRelative,
    updatePagePosition,
    batchUpdatePositions,
    deletePage,
    updatePageColor,
    addSection,
    removeSection,
    duplicateSection,
    moveSectionToPage,
    reorderSections,
    generateCopy,
    generateAllCopy,
    updateSectionCopy,
    updateSectionColor,
    startConnection,
    completeConnection,
    removeConnection,
    updateConnectionStyle,
    cancelConnection,
    duplicatePage,
    swapPagePositions,
    updatePageMeta,
    isLoading,
    isSaving,
    lastSaved,
    save,
  };
}
